"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: all[name]
    });
}
_export(exports, {
    SummaryData: function() {
        return SummaryData;
    },
    AnomalyGroup: function() {
        return AnomalyGroup;
    },
    doRangesOverlap: function() {
        return doRangesOverlap;
    },
    isSameBenchmark: function() {
        return isSameBenchmark;
    },
    isSameRevision: function() {
        return isSameRevision;
    },
    isSameBot: function() {
        return isSameBot;
    },
    isSameTest: function() {
        return isSameTest;
    },
    groupAnomalies: function() {
        return groupAnomalies;
    }
});
let SummaryData = class SummaryData {
    constructor(){
        this.startRevision = 0;
        this.endRevision = 0;
        this.bot = '';
        this.testsuite = '';
        this.test = '';
        this.delta = 0;
        this.isSummaryRegression = false;
        // AnomalyGroup is represented by an arbitrarily chosen bug.
        // We expect at most one distinct bug in a group.
        // After switching to skia-based backend, we will show
        // all bugs in a tooltip.
        this.bug = 0;
        this.numRegressions = 0;
        this.numImprovements = 0;
        this.calculated = false;
    }
};
let AnomalyGroup = class AnomalyGroup {
    constructor(){
        this.anomalies = [];
        this.summaryData = new SummaryData();
        this.expanded = false;
    }
};
function doRangesOverlap(a, b) {
    if (a.start_revision > b.start_revision) {
        [a, b] = [
            b,
            a
        ];
    }
    if (a.start_revision === null || a.end_revision === null || b.start_revision === null || b.end_revision === null) {
        return false;
    }
    return a.start_revision <= b.end_revision && a.end_revision >= b.start_revision;
}
/**
 * Helper method to group anomalies based on a predicate.
 *
 * It takes a list of anomalies, groups them, and then partitions the result
 * into groups containing multiple items and a flat list of anomalies that
 * remained in single-item groups.
 *
 * @param anomalies - The list of anomalies to group.
 * @param predicate - A function that returns true if two anomalies belong in the same group.
 * @returns An object containing the grouped anomalies and the remaining single anomalies.
 */ function groupAndPartition(anomalies, predicate) {
    if (!anomalies.length) {
        return {
            multiItemGroups: [],
            singleAnomalies: []
        };
    }
    // Use reduce to iterate once and create all groups.
    const allGroups = anomalies.reduce((groups, anomaly)=>{
        const existingGroup = groups.find((g)=>g.anomalies.every((other)=>predicate(anomaly, other)));
        if (existingGroup) {
            existingGroup.anomalies.push(anomaly);
        } else {
            const newGroup = new AnomalyGroup();
            newGroup.anomalies = [
                anomaly
            ];
            groups.push(newGroup);
        }
        return groups;
    }, []);
    // Now, partition the results into multi-item groups and singles.
    const multiItemGroups = [];
    const singleAnomalies = [];
    for (const group of allGroups){
        if (group.anomalies.length > 1) {
            multiItemGroups.push(group);
        } else {
            singleAnomalies.push(group.anomalies[0]);
        }
    }
    return {
        multiItemGroups,
        singleAnomalies
    };
}
function isSameBenchmark(a, b) {
    const testSuiteA = a.test_path.split('/').length > 2 ? a.test_path.split('/')[2] : '';
    const testSuiteB = b.test_path.split('/').length > 2 ? b.test_path.split('/')[2] : '';
    return testSuiteA === testSuiteB;
}
function isSameRevision(a, b) {
    return a.start_revision === b.start_revision && a.end_revision === b.end_revision;
}
function isSameBot(a, b) {
    const botA = a.test_path.split('/').length > 1 ? a.test_path.split('/')[1] : '';
    const botB = b.test_path.split('/').length > 1 ? b.test_path.split('/')[1] : '';
    return botA === botB;
}
function isSameTest(a, b) {
    const testA = a.test_path.split('/').length > 3 ? a.test_path.split('/')[3] : '';
    const testB = b.test_path.split('/').length > 3 ? b.test_path.split('/')[3] : '';
    return testA === testB;
}
function groupAnomalies(anomalyList, config) {
    // 1. Unconditional Override: Separate by Bug ID
    const withBugId = [];
    const withoutBugId = [];
    for (const anomaly of anomalyList){
        if (anomaly.bug_id && anomaly.bug_id > 0) {
            withBugId.push(anomaly);
        } else {
            withoutBugId.push(anomaly);
        }
    }
    // Group the bug_id ones
    const bugIdGroupMap = withBugId.reduce((map, anomaly)=>{
        const bugId = anomaly.bug_id;
        const group = map.get(bugId) || [];
        map.set(bugId, [
            ...group,
            anomaly
        ]);
        return map;
    }, new Map());
    const bugIdGroups = Array.from(bugIdGroupMap.values()).map((anomalies)=>{
        const group = new AnomalyGroup();
        group.anomalies = anomalies;
        return group;
    });
    // 2. Configurable Logic: Revision Grouping
    let revisionGroups = [];
    let singlesPool = [];
    switch(config.revisionMode){
        case 'EXACT':
            {
                // Only group if ranges are identical
                const result = groupAndPartition(withoutBugId, (a, b)=>isSameRevision(a, b));
                revisionGroups = result.multiItemGroups;
                singlesPool = result.singleAnomalies;
                break;
            }
        case 'OVERLAPPING':
            {
                const overlapResult = groupAndPartition(withoutBugId, (a, b)=>doRangesOverlap(a, b));
                revisionGroups = overlapResult.multiItemGroups;
                singlesPool = overlapResult.singleAnomalies;
                break;
            }
        case 'ANY':
            {
                // Treat all anomalies as one large group
                if (withoutBugId.length > 0) {
                    const group = new AnomalyGroup();
                    group.anomalies = withoutBugId;
                    revisionGroups = [
                        group
                    ];
                }
                singlesPool = [];
                break;
            }
    }
    // 3. Configurable Logic: Attribute Splitting
    const predicateMap = {
        BENCHMARK: isSameBenchmark,
        BOT: isSameBot,
        TEST: isSameTest
    };
    const predicates = Array.from(config.groupBy).map((c)=>predicateMap[c]).filter((p)=>!!p);
    let processedGroups = [];
    if (predicates.length > 0) {
        const combinedPredicate = (a, b)=>predicates.every((p)=>p(a, b));
        for (const group of revisionGroups){
            const { multiItemGroups, singleAnomalies } = groupAndPartition(group.anomalies, combinedPredicate);
            processedGroups.push(...multiItemGroups);
            singleAnomalies.forEach((a)=>{
                const singleGroup = new AnomalyGroup();
                singleGroup.anomalies = [
                    a
                ];
                processedGroups.push(singleGroup);
            });
        }
    } else {
        processedGroups = revisionGroups;
    }
    // 4. Remaining Singles
    const singlesGroups = singlesPool.map((a)=>{
        const group = new AnomalyGroup();
        group.anomalies = [
            a
        ];
        return group;
    });
    // 5. Final Assembly
    return [
        ...bugIdGroups,
        ...processedGroups,
        ...singlesGroups
    ];
}
